<template>
  <div class="performance">
    <!-- Two-Column Layout: Sources + Revenue -->
    <div class="perf-grid">
      <!-- Traffic Sources -->
      <v-card flat class="perf-card">
        <div class="perf-card-header">
          <h3 class="perf-card-title">Répartition Sources</h3>
          <v-chip small outlined :color="RMR_INDIGO" class="perf-badge">
            {{ isGa4 ? 'GA4' : 'Non disponible (GA4 uniquement)' }}
          </v-chip>
        </div>
        <div v-if="!isGa4" class="perf-empty">
          <v-icon small color="#94A3B8" class="mr-1">{{ icons.mdiInformationOutline }}</v-icon>
          La répartition par source de trafic est disponible uniquement avec la source GA4.
        </div>
        <div v-else-if="trafficSources.length > 0" class="source-list">
          <div v-for="source in trafficSources" :key="source.name" class="source-item">
            <div class="source-top">
              <div class="source-name">
                <span class="source-dot" :style="{ background: source.color }"></span>
                {{ source.name }}
              </div>
              <span class="source-percent">{{ source.percent }}%</span>
            </div>
            <div class="source-bar-track">
              <div
                class="source-bar-fill"
                :style="{ width: source.percent + '%', background: source.color }"
              ></div>
            </div>
            <div class="source-detail">
              <span>{{ formatNumber(source.sessions) }} sessions</span>
              <span class="source-ca">{{ formatCurrency(source.ca) }}</span>
            </div>
          </div>
        </div>
        <div v-else class="perf-empty">Aucune donnée disponible</div>
      </v-card>

      <!-- Revenue Distribution -->
      <v-card flat class="perf-card">
        <div class="perf-card-header">
          <h3 class="perf-card-title">Répartition Revenus</h3>
          <v-chip small outlined :color="RMR_INDIGO" class="perf-badge">Partenaires</v-chip>
        </div>
        <div class="revenue-chart-wrapper">
          <vue-apex-charts
            v-if="pieChartOptions && pieChartSeries.length > 0"
            type="donut"
            height="240"
            :options="pieChartOptions"
            :series="pieChartSeries"
          />
          <div v-else class="perf-empty">Aucune donnée disponible</div>
        </div>
        <div v-if="revenuePartners.length > 0" class="revenue-legend">
          <div v-for="item in revenuePartners" :key="item.name" class="revenue-legend-item">
            <span class="revenue-dot" :style="{ background: item.color }"></span>
            <span class="revenue-name">{{ item.name }}</span>
            <span class="revenue-value">{{ formatCurrency(item.value) }}</span>
            <span class="revenue-pct">{{ item.percent }}%</span>
          </div>
        </div>
      </v-card>
    </div>

    <!-- Top Sites by RPM -->
    <v-card flat class="perf-card">
      <div class="perf-card-header">
        <div>
          <h3 class="perf-card-title">Top Sites par RPM</h3>
          <span class="perf-card-subtitle">Classement des 10 meilleurs sites</span>
        </div>
        <v-chip small outlined :color="RMR_INDIGO" class="perf-badge">
          {{ trafficSourceLabel }}
        </v-chip>
      </div>
      <div class="rpm-chart-wrapper">
        <vue-apex-charts
          v-if="rpmBarChartOptions && topSites.length > 0"
          type="bar"
          height="320"
          :options="rpmBarChartOptions"
          :series="rpmBarChartSeries"
        />
        <div v-else class="perf-empty">Aucune donnée disponible</div>
      </div>
    </v-card>

    <!-- Top Articles Table -->
    <v-card flat class="perf-card">
      <div class="perf-card-header">
        <div>
          <h3 class="perf-card-title">Top 10 Articles</h3>
          <span class="perf-card-subtitle">Par sessions & revenus</span>
        </div>
        <v-chip small outlined :color="RMR_INDIGO" class="perf-badge">
          {{ isGa4 ? 'GA4' : 'Non disponible (GA4 uniquement)' }}
        </v-chip>
      </div>

      <div v-if="!isGa4" class="perf-empty">
        <v-icon small color="#94A3B8" class="mr-1">{{ icons.mdiInformationOutline }}</v-icon>
        Le classement des articles est disponible uniquement avec la source GA4.
      </div>
      <v-data-table
        v-else
        :headers="articleHeaders"
        :items="topArticles"
        hide-default-footer
        class="articles-table"
      >
        <template v-slot:item.rank="{ item }">
          <div class="rank-badge" :class="{ 'rank-top': item.rank <= 3 }">
            {{ item.rank }}
          </div>
        </template>
        <template v-slot:item.title="{ item }">
          <div class="article-title-cell">
            <span class="article-title">{{ item.title }}</span>
            <span class="article-category">{{ item.category }}</span>
          </div>
        </template>
        <template v-slot:item.sessions="{ item }">
          <span class="td-sessions">{{ formatNumber(item.sessions) }}</span>
        </template>
        <template v-slot:item.ca="{ item }">
          <span class="td-ca">{{ formatCurrency(item.ca) }}</span>
        </template>
        <template v-slot:item.rpm="{ item }">
          <span class="td-rpm">{{ item.rpm.toFixed(2) }}€</span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { mdiInformationOutline } from '@mdi/js'
import { computed, ref, watch } from '@vue/composition-api'
import VueApexCharts from 'vue-apexcharts'
import useProxy from '@/hooks/useProxy'
import { RMR_INDIGO } from '@/styles/tokens'

const COMPONENT_NAME = 'DashboardV2Performance'

const SOURCE_COLORS = [
  '#4285F4', '#0D9488', '#7C3AED', '#EA580C', '#94A3B8',
  '#312E81', '#059669', '#D97706', '#0284C7', '#A855F7',
]

const ARTICLE_HEADERS = [
  { text: '#',        value: 'rank',     align: 'center', width: 60, sortable: false },
  { text: 'Article',  value: 'title',    sortable: false, width: '70%' },
  { text: 'Sessions', value: 'sessions', align: 'end', sortable: true, width: '10%' },
  { text: 'CA Est.',  value: 'ca',       align: 'end', sortable: true, width: '10%' },
  { text: 'RPM',      value: 'rpm',      align: 'end', sortable: true, width: '10%' },
]

export default {
  name: COMPONENT_NAME,
  components: { VueApexCharts },
  setup() {
    const { store } = useProxy()

    // => CONST
    const pieChartOptions = ref(null)
    const pieChartSeries = ref([])
    const rpmBarChartOptions = ref(null)
    const rpmBarChartSeries = ref([])

    // => METHODS
    const formatNumber = (num) => (num || 0).toLocaleString('fr-FR')
    const formatCurrency = (num) =>
      (num || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + '€'

    const buildPieChart = (partners) => {
      pieChartSeries.value = partners.map((p) => p.percent)
      pieChartOptions.value = {
        chart: { type: 'donut', fontFamily: 'Inter, system-ui, sans-serif' },
        colors: partners.map((p) => p.color),
        labels: partners.map((p) => p.name),
        plotOptions: {
          pie: {
            donut: {
              size: '72%',
              labels: {
                show: true,
                name:  { show: true, fontSize: '12px', fontWeight: 600, color: '#64748B' },
                value: { show: true, fontSize: '20px', fontWeight: 800, color: '#0F172A', formatter: (val) => val + '%' },
                total: { show: true, label: 'Total', fontSize: '11px', fontWeight: 600, color: '#94A3B8', formatter: () => '100%' },
              },
            },
          },
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        stroke: { width: 2, colors: ['#fff'] },
        tooltip: { y: { formatter: (val) => val + '%' } },
      }
    }

    const buildRpmBarChart = (sites) => {
      rpmBarChartSeries.value = [{ name: 'RPM', data: sites.map((s) => s.rpm) }]
      rpmBarChartOptions.value = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif' },
        colors: ['#6366F1'],
        plotOptions: {
          bar: {
            horizontal: true, borderRadius: 6, barHeight: '60%',
            dataLabels: { position: 'right' },
          },
        },
        fill: {
          type: 'gradient',
          gradient: { shade: 'light', type: 'horizontal', shadeIntensity: 0.25, gradientToColors: ['#7C3AED'], opacityFrom: 0.85, opacityTo: 1, stops: [0, 100] },
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => val.toFixed(2) + '€',
          offsetX: 6,
          style: { fontSize: '11px', fontWeight: 700, colors: ['#312E81'] },
          background: { enabled: false },
        },
        xaxis: {
          categories: sites.map((s) => s.name),
          labels: { style: { colors: '#94A3B8', fontSize: '10px', fontWeight: 600 }, formatter: (val) => val.toFixed(2) + '€' },
          axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: '#475569', fontSize: '11px', fontWeight: 600 }, maxWidth: 150 } },
        grid: { borderColor: '#F1F5F9', xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
        tooltip: { y: { formatter: (val) => val.toFixed(2) + '€' } },
      }
    }

    // => COMPUTED
    const isGa4 = computed(() => store.getters['dashboardV2/getTrafficSource'] === 'ga4')
    const trafficSourceLabel = computed(() =>
      store.getters['dashboardV2/getTrafficSource'] === 'ga4' ? 'GA4' : 'Wysistat'
    )
    const performanceData = computed(() => store.getters['dashboardV2/getPerformanceData'])
    const revenuePartners = computed(() => performanceData.value?.partners || [])
    const trafficSources = computed(() => {
      const sources = performanceData.value?.sources || []
      return sources.map((source, idx) => ({
        ...source,
        color: SOURCE_COLORS[idx % SOURCE_COLORS.length],
      }))
    })
    const topSites = computed(() => performanceData.value?.topSites || [])
    const topArticles = computed(() => performanceData.value?.topArticles || [])

    // => WATCH
    watch(performanceData, () => {
      buildPieChart(revenuePartners.value)
      buildRpmBarChart(topSites.value)
    }, { immediate: true })

    // => DATA
    return {
      icons: { mdiInformationOutline },
      isGa4,
      pieChartOptions,
      pieChartSeries,
      rpmBarChartOptions,
      rpmBarChartSeries,
      articleHeaders: ARTICLE_HEADERS,
      trafficSourceLabel,
      revenuePartners,
      trafficSources,
      topSites,
      topArticles,
      formatNumber,
      formatCurrency,
      RMR_INDIGO,
    }
  },
}
</script>

<style lang="scss" scoped>
.performance {
  display: flex;
  flex-direction: column;
  gap: $rmr-space-xl;
}

.perf-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $rmr-space-xl;
}

.perf-card {
  border-radius: $rmr-radius-2xl !important;
  padding: $rmr-space-xl;
  box-shadow: $rmr-shadow-card !important;
}

.perf-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: $rmr-space-sm;
  border-bottom: 1px solid $rmr-border;
}

.perf-card-title {
  font-size: $rmr-fs-title;
  font-weight: 700;
  text-transform: uppercase;
  color: $rmr-indigo-dark;
  letter-spacing: 0.5px;
  margin: 0;
}

.perf-card-subtitle {
  display: block;
  font-size: $rmr-fs-base;
  color: $rmr-text-subtle;
  font-weight: 500;
  margin-top: 2px;
}

.perf-badge {
  font-weight: 700 !important;
  font-size: $rmr-fs-sm !important;
}

.source-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.source-item {
  display: flex;
  flex-direction: column;
  gap: $rmr-space-xs;
}

.source-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.source-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: $rmr-fs-body;
  font-weight: 600;
  color: $rmr-text-medium;
}

.source-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.source-percent {
  font-size: $rmr-fs-body;
  font-weight: 800;
  color: $rmr-text-primary;
}

.source-bar-track {
  height: 8px;
  background: $rmr-border;
  border-radius: $rmr-radius-pill;
  overflow: hidden;
}

.source-bar-fill {
  height: 100%;
  border-radius: $rmr-radius-pill;
  transition: width 0.8s ease;
}

.source-detail {
  display: flex;
  justify-content: space-between;
  font-size: $rmr-fs-sm;
  color: $rmr-text-subtle;
  font-weight: 500;
}

.source-ca {
  font-weight: 700;
  color: $rmr-text-muted;
}

.revenue-chart-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: $rmr-space-md;
}

.revenue-legend {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: $rmr-space-md;
  border-top: 1px solid $rmr-border;
}

.revenue-legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: $rmr-fs-md;
}

.revenue-dot {
  width: 10px;
  height: 10px;
  border-radius: 4px;
  flex-shrink: 0;
}

.revenue-name {
  font-weight: 600;
  color: $rmr-text-secondary;
  flex: 1;
}

.revenue-value {
  font-weight: 700;
  color: $rmr-text-primary;
  min-width: 70px;
  text-align: right;
}

.revenue-pct {
  font-weight: 800;
  color: $rmr-indigo;
  min-width: 36px;
  text-align: right;
}

.rpm-chart-wrapper {
  min-height: 300px;
}

.perf-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  color: $rmr-text-subtle;
  font-size: $rmr-fs-body;
  font-weight: 600;
}

.articles-table {
  border-radius: $rmr-radius-lg;
  overflow: hidden;

  ::v-deep .v-data-table-header th {
    font-size: $rmr-fs-sm !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.8px !important;
    color: $rmr-text-subtle !important;
    background: $rmr-surface-2 !important;
    border-bottom: 1px solid $rmr-border !important;
    padding: $rmr-space-sm $rmr-space-md !important;
  }

  ::v-deep tbody tr {
    transition: background-color 0.15s ease;

    &:hover { background: $rmr-surface-1 !important; }
  }

  ::v-deep tbody td {
    padding: $rmr-space-sm $rmr-space-md !important;
    font-size: $rmr-fs-body !important;
    border-bottom: 1px solid $rmr-surface-2 !important;
  }
}

.rank-badge {
  width: 28px;
  height: 28px;
  border-radius: $rmr-radius-sm;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: $rmr-fs-md;
  color: $rmr-text-muted;
  background: $rmr-border;
  margin: 0 auto;

  &.rank-top {
    background: linear-gradient(135deg, $rmr-indigo, $rmr-rpm);
    color: white;
  }
}

.article-title-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.article-title {
  font-weight: 600;
  color: $rmr-text-base;
  font-size: $rmr-fs-body;
  line-height: 1.3;
}

.article-category {
  font-size: $rmr-fs-sm;
  font-weight: 700;
  text-transform: uppercase;
  color: $rmr-text-subtle;
  letter-spacing: 0.5px;
}

.td-sessions { font-weight: 700; color: $rmr-sessions; }
.td-ca       { font-weight: 700; color: $rmr-ca; }
.td-rpm      { font-weight: 700; color: $rmr-rpm; }

@media (max-width: 960px) {
  .perf-grid { grid-template-columns: 1fr; }
}
</style>
